import React from 'react'
import { createRoot } from 'react-dom/client'
import ExportPreview from '../components/export/ExportPreview'
import type { Captain } from 'types/fpl/elements'
import type { Chip } from 'types/fpl/chips'
import type { InterestScore, LeagueTemplate, TemplatinessStats } from 'api/league'
import type { LeagueTransferStats } from 'types/fpl/transfers'
import { domToBlob } from 'modern-screenshot'
import { colors } from 'constants/colors'
import { shirtImageSrc } from 'utils/shirtSrc'

interface ExportData {
  leagueName: string
  gameweek: string
  captains: Captain[]
  chips: Chip[]
  totalTeams: number
  isDarkMode?: boolean
  interest?: InterestScore | null
  leagueTemplate?: LeagueTemplate | null
  templatiness?: TemplatinessStats | null
  transferStats?: LeagueTransferStats | null
}

const nextFrame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

const waitImages = async (rootEl: HTMLElement) => {
  const imgs = Array.from(rootEl.querySelectorAll('img'))
  
  await Promise.all(
    imgs.map((img) => {
      const htmlImg = img as HTMLImageElement
      if (htmlImg.complete) return Promise.resolve()
      
      return new Promise<void>(resolve => {
        htmlImg.onload = () => resolve()
        htmlImg.onerror = () => resolve()
        setTimeout(resolve, 2000)
      })
    })
  )
  
  await nextFrame()
  await new Promise(resolve => setTimeout(resolve, 300))
}

const supportsFileShare = (file: File) => {
  const navigatorWithShare = navigator as unknown as {
    canShare?: (data: { files: File[] }) => boolean
    share?: (data: { files: File[]; title: string }) => Promise<void>
  }

  return Boolean(
    navigatorWithShare.share &&
    (!navigatorWithShare.canShare || navigatorWithShare.canShare({ files: [file] })),
  )
}

const downloadBlob = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

export interface ExportImage {
  blob: Blob
  filename: string
}

export const saveImage = async (
  { blob, filename }: ExportImage,
  useNativeShare = false,
): Promise<void> => {
  const image = new File([blob], filename, { type: 'image/png' })
  const navigatorWithShare = navigator as unknown as {
    share?: (data: { files: File[]; title: string }) => Promise<void>
  }

  if (useNativeShare && supportsFileShare(image)) {
    try {
      await navigatorWithShare.share!({ files: [image], title: filename })
      return
    } catch (error) {
      // Closing the share sheet is expected; in that case no fallback is needed.
      if (error instanceof DOMException && error.name === 'AbortError') return
      throw error
    }
  }

  downloadBlob(blob, filename)
}

export const createLeagueInsightsImage = async (data: ExportData): Promise<ExportImage> => {
  const container = document.createElement('div')

  try {
    const shirtImages: Record<number, string> = {}
    const gkShirtImages: Record<number, string> = {}

    const addShirt = (tid: number, isGk = false) => {
      if (typeof tid !== 'number' || tid <= 0) return
      if (isGk) {
        gkShirtImages[tid] = shirtImageSrc(tid, true)
      } else {
        shirtImages[tid] = shirtImageSrc(tid)
      }
    }

    for (const c of data.captains) {
      addShirt(c.player.team)
    }

    for (const row of data.leagueTemplate?.players ?? []) {
      const tid = row.player?.team ?? 0
      const isGk = Number(row.player?.element_type) === 1
      addShirt(tid, isGk)
    }

    container.style.position = 'fixed'
    container.style.left = '0'
    container.style.top = '0'
    container.style.width = '1100px'
    container.style.opacity = '0'
    container.style.pointerEvents = 'none'
    container.style.zIndex = '-1'
    document.body.appendChild(container)

    const root = createRoot(container)
    root.render(
      React.createElement(ExportPreview, {
        leagueName: data.leagueName,
        gameweek: data.gameweek,
        captains: data.captains,
        chips: data.chips,
        totalTeams: data.totalTeams,
        isDarkMode: data.isDarkMode ?? false,
        shirtImages,
        gkShirtImages,
        interest: data.interest,
        leagueTemplate: data.leagueTemplate,
        templatiness: data.templatiness,
        transferStats: data.transferStats,
      })
    )

    // commit + layout + paint
    await nextFrame()
    await nextFrame()

    // fonts
    if (document.fonts?.ready) await document.fonts.ready

    const exportElement = container.querySelector('#export-preview') as HTMLElement | null
    if (!exportElement) throw new Error('Export preview element not found')

    await waitImages(exportElement)
    await nextFrame()

    const bgColor = data.isDarkMode ? colors.RICH_BLACK : colors.MOONSTONE
    const image = await domToBlob(exportElement, {
      scale: 2,
      backgroundColor: bgColor,
      type: 'image/png',
    })

    const filename = `FPL_${data.leagueName.replace(/[^a-z0-9]/gi, '_')}_GW${data.gameweek}.png`
    root.unmount()
    return { blob: image, filename }
  } finally {
    if (container.parentNode) container.parentNode.removeChild(container)
  }
}

export const exportLeagueInsightsImage = async (data: ExportData): Promise<void> => {
  const image = await createLeagueInsightsImage(data)
  await saveImage(image)
}
